import {
  Component,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { environment } from '../../../environment/environment';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import { EventCateringApprovalListItemDTO } from '../../../dto/event/event-catering-approval-list-item.dto';
import { EventCateringApproveRequestDTO } from '../../../dto/event/event-catering-approve-request.dto';
import {
  DunaCateringPreferencesDialogComponent,
  DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
} from './duna-catering-preferences-dialog.component';

@Component({
  selector: 'app-duna-catering',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    DynamicTableComponent,
  ],
  templateUrl: './duna-catering.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-catering.component.scss',
})
export class DunaCateringComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private approveListener = (e: Event) =>
    this.handleApproveEvent(e as CustomEvent);
  private preferencesListener = (e: Event) =>
    this.handlePreferencesEvent(e as CustomEvent);

  @ViewChild('pendingTable') pendingTable?: DynamicTableComponent;
  @ViewChild('approvedTable') approvedTable?: DynamicTableComponent;

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private componentDataSourceService: ComponentDataSourceService
  ) {}

  ngOnInit(): void {
    window.addEventListener(
      'duna-catering-approve',
      this.approveListener as EventListener
    );
    window.addEventListener(
      'duna-catering-preferences',
      this.preferencesListener as EventListener
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'duna-catering-approve',
      this.approveListener as EventListener
    );
    window.removeEventListener(
      'duna-catering-preferences',
      this.preferencesListener as EventListener
    );
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleApproveEvent(event: CustomEvent): void {
    const row = event.detail as EventCateringApprovalListItemDTO;
    this.confirmAndApprove(row);
  }

  private handlePreferencesEvent(event: CustomEvent): void {
    const row = event.detail as EventCateringApprovalListItemDTO;
    this.openPreferences(row);
  }

  private confirmAndApprove(row: EventCateringApprovalListItemDTO): void {
    const title =
      row.approval_status === 'needs_reapproval'
        ? 'Gen-godkend forplejning?'
        : 'Godkend forplejning?';

    const dialogRef = this.dialog.open(ConfirmResetDialogComponent, {
      width: '650px',
      data: {
        title,
        message: 'Dette sender en notifikation til eventets instruktører.',
        details: [
          `Event: ${row.event_name || row.guid}`,
          `Afventende antal (inkl. instruktører): ${row.current_pending_user_count}`,
          row.approved_pending_user_count !== null &&
          row.approved_pending_user_count !== undefined
            ? `Tidligere godkendt antal: ${row.approved_pending_user_count}`
            : 'Tidligere godkendt antal: (ingen)',
        ],
        confirmText: 'Godkend',
        cancelText: 'Annuller',
        confirmColor: 'primary',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.approve(row);
      }
    });
  }

  private approve(row: EventCateringApprovalListItemDTO): void {
    const payload: EventCateringApproveRequestDTO = {
      event_id: row.event_id,
    };

    this.http
      .post(`${environment.apiUrl}/events/catering/approve`, payload)
      .subscribe({
        next: () => {
          // Refresh both tables (preserves paging/sort/search state)
          this.pendingTable?.refreshTable();
          this.approvedTable?.refreshTable();
          // Fallback for other components that depend on the old refreshData mechanism
          this.componentDataSourceService.refreshData();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error approving catering:', error);
        },
      });
  }

  private openPreferences(row: EventCateringApprovalListItemDTO): void {
    this.dialog.open(DunaCateringPreferencesDialogComponent, {
      ...DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
      data: { row },
    });
  }
}
